import sanitizeSVG from '@mattkrick/sanitize-svg';
import {
  ACCEPT_FAVICON_MIME_TYPES_REGEX,
  type ACCEPT_IMAGE_FILES,
  ACCEPT_IMAGE_MIME_TYPES_REGEX,
} from '../constants';

type MIMEValidationType = 'image' | 'favicon';

export type ValidatorProps = {
  validFileFormats: typeof ACCEPT_IMAGE_FILES;
  validateMIMEType?: MIMEValidationType;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  maxSizeInBytesSvg?: number;
};

export const createImageValidator = ({
  validFileFormats,
  validateMIMEType,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  maxSizeInBytesSvg,
}: ValidatorProps) => {
  return (file: File) =>
    new Promise<File | void>((resolve, reject) => {
      if (!file) {
        resolve();
      }

      let hasValidFileFormat = false;
      const imageType = file.type;
      const imageFormat = file.name.replace(/^.*\./, '.').toLowerCase();

      if (validateMIMEType === 'image') {
        hasValidFileFormat = imageType.match(ACCEPT_IMAGE_MIME_TYPES_REGEX) !== null;
      } else if (validateMIMEType === 'favicon') {
        hasValidFileFormat = imageType.match(ACCEPT_FAVICON_MIME_TYPES_REGEX) !== null;
      } else {
        hasValidFileFormat = validFileFormats.includes(imageFormat);
      }

      if (imageType === 'image/svg+xml') {
        if (maxSizeInBytesSvg && file.size > maxSizeInBytesSvg) {
          reject({ code: 'error.size' });
        }
        sanitizeSVG(file)
          .then((file: File | null | Buffer) => {
            if (!file) {
              reject({ code: 'error.unsafe' });
            } else {
              resolve(file as File);
            }
          })
          .catch(() => {}); // eslint-disable-line @typescript-eslint/no-empty-function
      }

      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(image.src); // cleanup
        if (!hasValidFileFormat) {
          reject({ code: 'error.fileformat' });
        } else if (!!minWidth && image.width < minWidth) {
          reject({ code: 'error.dimensions' });
        } else if (!!minHeight && image.height < minHeight) {
          reject({ code: 'error.dimensions' });
        } else if (!!maxWidth && image.width > maxWidth) {
          reject({ code: 'error.dimensions' });
        } else if (!!maxHeight && image.height > maxHeight) {
          reject({ code: 'error.dimensions' });
        } else {
          resolve(file);
        }
      };
      image.onerror = () => {
        if (hasValidFileFormat) {
          // Could be format that is not supported to load in current browser
          // Let it pass.
          resolve(file);
        } else {
          reject({ code: 'error.fileformat' });
        }
      };
      const url = URL.createObjectURL(file);
      image.src = url;
    });
};
